/**
 * Redux slice for the current gig worker's own requests.
 *
 * Wraps GET /api/gig/myRequests/{userId}?filter=all|today|thisweek.
 * Backend service impl is pending — currently returns []. UI should
 * tolerate an empty array.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';
import { GigFilter, GigShiftRequest } from '../../types/gigApi';

interface GigMyRequestsState {
    requests: GigShiftRequest[];
    loading: boolean;
    error: string | null;
    filter: GigFilter;
}

const initialState: GigMyRequestsState = {
    requests: [],
    loading: false,
    error: null,
    filter: 'all',
};

/**
 * Fetch the current gig worker's own requests.
 * Uses: GET /api/gig/myRequests/{userId}?filter=all|today|thisweek
 */
export const fetchGigMyRequests = createAsyncThunk(
    'gigMyRequests/fetch',
    async (
        { userId, filter = 'all' }: { userId: string; filter?: GigFilter },
        { rejectWithValue }
    ) => {
        try {
            const params = new URLSearchParams({ filter });
            const response = await API_FETCH(
                `${ENDPOINTS.GIG_MY_REQUESTS}/${encodeURIComponent(userId)}?${params.toString()}`
            );
            return Array.isArray(response) ? (response as GigShiftRequest[]) : [];
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to fetch gig requests');
        }
    }
);

const gigMyRequestsSlice = createSlice({
    name: 'gigMyRequests',
    initialState,
    reducers: {
        setFilter: (state, action) => {
            state.filter = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGigMyRequests.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchGigMyRequests.fulfilled, (state, action) => {
                state.loading = false;
                state.requests = action.payload;
            })
            .addCase(fetchGigMyRequests.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { setFilter, clearError } = gigMyRequestsSlice.actions;
export default gigMyRequestsSlice.reducer;
